import React, { useEffect, useMemo, useRef, useState } from 'react';
import DocFileIconButton from '../../components/docs/DocFileIconButton';
import DocPreviewLightbox from '../pro/components/docs/DocPreviewLightbox';
import {
  emailEstimate, emailInvoice, invoiceViewUrl, loadInvoiceOptions, printInvoices,
  printInvoicesNamed, renderEstimate,
  type InvoiceBackupDocument, type InvoiceClass, type InvoiceOptionsData,
} from '../../services/accountingInvoiceSelection';
import {
  emailCreditMemo, loadCreditMemoOptions, renderCreditMemo,
  type CreditMemoOptionsData,
} from '../../services/creditMemo.service';
import { saveAs } from '../../utils/saveFile';
import { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import PrinterSelect from '../../components/printing/PrinterSelect';
import { PRINT_SEND_COMPONENT_ID } from '../../components/printing/PrintButton';
import { lastUsedPrinter } from '../../components/printing/lastUsedPrinter';
import { usePrintDefaults } from '../../hooks/usePrintDefaults';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import printingService from '../../services/printing.service';

/**
 * Invoice Options — the port of V1's `viewinvoiceoptions` (main.pl:19443-19612).
 *
 * One modal, two modes, exactly as in V1:
 *  * an invoice that EXISTS — tick what to do with it and Go.
 *  * an invoice about to be CREATED — tick what to do, and creating happens first.
 * And a third shape that replaces both: when the Bill To bills each pro separately, V1
 * drops the options entirely and asks only for confirmation, because a run that produces
 * forty invoices neither prints nor e-mails (main.pl:19542). That condition is now
 * `billtos.invoiceseparately`, so the server answers it as `invoicesSeparately`.
 *
 * Print has two routes. The browser's print dialog over the archived PDF is always there.
 * For users holding the `print-send` component, Print can instead go the way V1 did —
 * spooled server-side to one of the named laser printers in cms.printers, now over IPP
 * to the same CUPS server `lpr -P` fed. That route is preselected when the user has a
 * default laser printer in their settings, because that is what V1 did with
 * `default_printer`. An ESTIMATE is browser-only: V1's `$printtest` never spooled either.
 *
 * A fourth shape, `creditmemo`, serves V1's `viewcreditmemooptions` (main.pl:19221) from
 * the Rebill/Credit modal. V1 draws that screen from the same template as this one and
 * offers the same five actions, so it shares the dialog rather than cloning it; like
 * `estimate` it has no Documents picker and no per-pro split.
 */
export type InvoiceOptionsMode =
  | { kind: 'existing'; invoiceHistoryRecno: number }
  | {
      kind: 'create';
      invoiceRecnos: number[];
      /** How many invoices the run will raise, and how many charges the holds refuse.
       *  Supplied by the page, which has already evaluated both to decide whether to
       *  offer the button at all — so the confirmation can state the outcome without
       *  paying for a second review. */
      willCreate?: number;
      willHold?: number;
    }
  /** V1's $printtest: the same five actions over a PDF of an invoice that is never
   *  created. No Documents picker and no per-pro split — see the `estimate` service. */
  | { kind: 'estimate'; invoiceRecnos: number[] }
  /** A credit memo, keyed on the rebill/credit record it was opened from. Creates
   *  nothing; the server resolves which records the memo covers. */
  | { kind: 'creditmemo'; pronumber: number; recno: number };

export interface InvoiceOptionsChoice {
  view: boolean;
  print: boolean;
  toSelf: boolean;
  toBillto: string[];
  toOther: string;
  fileRecnos: number[];
  /** Separated runs only: e-mail each raised invoice to the Bill To. */
  emailInvoices: boolean;
}

const message = (e: unknown, fallback: string): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return e instanceof Error ? e.message : fallback;
};

const Check: React.FC<{
  checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children: React.ReactNode;
}> = ({ checked, onChange, disabled, children }) => (
  <label className={`flex items-start gap-2 ${disabled ? 'opacity-50' : ''}`}>
    <input type="checkbox" checked={checked} disabled={disabled}
      onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
    <span className="text-sm text-body">{children}</span>
  </label>
);

const InvoiceOptionsDialog: React.FC<{
  mode: InvoiceOptionsMode;
  billto: number;
  invoiceClass?: InvoiceClass;
  title: string;
  /** Runs before the actions in create mode; returns the new invoice history recnos. */
  onConfirm: (choice: InvoiceOptionsChoice) => Promise<number[]>;
  onClose: () => void;
  /** Every action succeeded. The dialog closes itself and hands the outcome to the host,
   *  which shows it on the page — parking a finished modal whose only control is Close
   *  makes the user dismiss something that has nothing left to say. `created` is true
   *  only when invoices were actually raised, and `recnos` names them: the page was
   *  opened on a masterpro that no longer has uninvoiced charges, so it has to move to
   *  the invoice or it renders "No orders are attached to this invoice". */
  onDone?: (result: { note: string; created: boolean; recnos: number[] }) => void;
  /** Stacking layer for the overlay. The default sits above a page; a caller that opens
   *  this from inside another modal has to name a layer above that one (the Rebill/
   *  Credit modal is z-[130]), or the dialog paints behind the modal that opened it. */
  layerClassName?: string;
  /** Suggested name (no extension) for the downloaded PDF. The invoice NUMBER lives on
   *  the page, not in the options payload, so the host supplies it; without one the file
   *  falls back to the history recno. A credit memo needs no `pdfName` — it carries its
   *  own document number in the options payload. */
  pdfName?: string;
}> = ({ mode, billto, invoiceClass = '', title, onConfirm, onClose, onDone,
        layerClassName = 'z-50', pdfName }) => {
  const [data, setData] = useState<InvoiceOptionsData | null>(null);
  /** Only in `creditmemo` mode — what the memo covers, for the header line. */
  const [memo, setMemo] = useState<CreditMemoOptionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const running = useRef(false);

  const [view, setView] = useState(false);
  const [print, setPrint] = useState(false);
  // Server-side printing (CUPS). Offered only behind the print-send component, and never
  // for an estimate. `printTarget` is decided once the user's defaults are known: a
  // saved default laser printer means "send to printer", as V1 preselected
  // gwi.users.default_printer; no default means the browser dialog, as 360 did before.
  const canSendToPrinter = useResolvedAccessLevel(PRINT_SEND_COMPONENT_ID, true) > 0
    && mode.kind !== 'estimate';
  const { defaults: printDefaults, loading: printDefaultsLoading } = usePrintDefaults();
  const [printTarget, setPrintTarget] = useState<'browser' | 'printer' | null>(null);
  const [printer, setPrinter] = useState('');
  useEffect(() => {
    if (!canSendToPrinter || printDefaultsLoading || printTarget !== null) return;
    const remembered = lastUsedPrinter.get('LASER');
    const preset = remembered || printDefaults.laser || '';
    setPrinter(preset);
    setPrintTarget(preset ? 'printer' : 'browser');
  }, [canSendToPrinter, printDefaultsLoading, printDefaults.laser, printTarget]);
  const sendToPrinter = print && canSendToPrinter && printTarget === 'printer';
  /** Print is ticked, routed to a printer, and no printer is chosen — Go must wait. */
  const printerMissing = sendToPrinter && !printer;
  const [toSelf, setToSelf] = useState(false);
  // Defaults ON: the Bill To was deliberately configured to receive these, so the
  // unsurprising outcome is that they go. Unticking is the deliberate act.
  const [emailEach, setEmailEach] = useState(true);
  const [toBillto, setToBillto] = useState(false);
  const [billtoPicked, setBilltoPicked] = useState<string[]>([]);
  const [toOther, setToOther] = useState(false);
  const [otherEmail, setOtherEmail] = useState('');
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // A credit memo is keyed on its rebill/credit record, not on a charge selection, and
    // its options are a strict subset — so it answers from its own endpoint and is
    // adapted onto the same shape the rest of the dialog reads.
    const load: Promise<InvoiceOptionsData> = mode.kind === 'creditmemo'
      ? loadCreditMemoOptions(mode.pronumber, mode.recno).then((m) => {
          if (alive) setMemo(m);
          return {
            billto: m.billto, billtoEmails: m.billtoEmails, userEmail: m.userEmail,
            invoicesSeparately: false, emailsEachInvoice: false, documents: [], documentTypes: [],
          };
        })
      : loadInvoiceOptions({
          billto,
          invoiceClass,
          invoiceHistoryRecno: mode.kind === 'existing' ? mode.invoiceHistoryRecno : undefined,
          invoiceRecnos: mode.kind === 'existing' ? undefined : mode.invoiceRecnos,
        });
    load
      .then((d) => { if (alive) { setData(d); setError(''); } })
      .catch((e) => { if (alive) setError(message(e, 'Could not load invoice options.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // The selection is fixed for the life of the dialog; re-fetching on every keystroke
    // would discard the user's ticks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allDocs = useMemo(
    () => (data?.documents ?? []).flatMap((pro) => pro.documents),
    [data],
  );
  const isEstimate = mode.kind === 'estimate';
  const isCreditMemo = mode.kind === 'creditmemo';
  // Neither an estimate nor a credit memo has backup paperwork to offer: V1 reaches
  // `doInvoiceOptions`/`doCreditMemoOptions` without an `$attach_files` argument on both
  // paths (invoicepdf.pl:225, :1576).
  const noDocuments = isEstimate || isCreditMemo;
  // One estimate covers the whole selection however the Bill To would be invoiced: V1's
  // printtest arm renders the full recno list in a single pass (lib_invoice.pl:2022)
  // where the real arm renders per created invoice. So the split confirmation, which is
  // a warning about what CREATING would do, has nothing to warn about here — and a
  // credit memo creates nothing at all.
  // CREATE ONLY. This block is a warning about what pressing the button WILL DO, and
  // it replaces the actions with a bare confirmation. On an invoice that already
  // exists there is nothing to warn about — it was raised under whatever grouping
  // applied at the time, and viewing, printing and e-mailing it are all still on
  // offer. Before this, opening an existing invoice for one of the 14 bill-tos that
  // split showed "Each set of charges on this Bill To is invoiced separately" over a
  // "Create Invoices" button that did nothing. `estimate` and `creditmemo` create
  // nothing either, which is what `noDocuments` already covered.
  const splits = mode.kind === 'create' && (data?.invoicesSeparately ?? false);
  // The Bill To is configured to mail every invoice a separated run raises. The checkbox
  // lets a user decline THIS run; it cannot start sending for a Bill To that is not set
  // up for it, because the server gates on the column either way.
  const canEmailEach = splits && (data?.emailsEachInvoice ?? false);
  const willCreate = mode.kind === 'create' ? mode.willCreate : undefined;
  const willHold = mode.kind === 'create' ? mode.willHold : undefined;
  const emails = data?.billtoEmails ?? [];
  const chosenEmails = toBillto ? billtoPicked.filter((e) => emails.includes(e)) : [];
  // V1 will not submit with nothing ticked (main.pl:19645). In the split case the only
  // thing on offer IS the confirmation, so the button is always live.
  // Create mode cannot offer the BUTTON — the invoices do not exist until Go runs —
  // so there it is an action ticked alongside View and Print and performed afterwards.
  // The trade is real and deliberate: by the time it runs, the click's transient
  // activation is spent and `showSaveFilePicker` can no longer open, so a user with
  // Show Save As on still lands in Downloads. The FILENAME is right either way, which
  // is what the picker was being relied on to fix.
  const [saveAfterCreate, setSaveAfterCreate] = useState(false);
  // The document being previewed. The lightbox is the pro Docs tab's, addressed by the
  // cms_map recno the options payload now carries — `fileRecno` is a fileinfo recno and
  // names a different row, which would preview somebody else's file.
  const [previewDoc, setPreviewDoc] = useState<InvoiceBackupDocument | null>(null);

  const canGo = (splits
    || view || print || saveAfterCreate
    || toSelf || (toBillto && chosenEmails.length > 0) || (toOther && !!otherEmail.trim()))
    && !printerMissing;

  const toggleDocs = (recnos: number[], on: boolean) => {
    setPicked((current) => {
      const next = new Set(current);
      recnos.forEach((r) => (on ? next.add(r) : next.delete(r)));
      return next;
    });
  };

  /**
   * Save the invoice PDF (#33683).
   *
   * V1's View Invoice looks like a download, but that is not what it is asking for:
   * invoicepdf.pl::doInvoiceOptions opens a `_blank` tab, and inline_documents.pl:450
   * serves EVERY file it hands out as `Content-Disposition: attachment`, so the tab
   * downloads and blanks. The tab was the intent; the download is the file-serving
   * layer showing through. View here does what V1 meant, and saving the file is its own
   * action — so this is a sixth option V1's five-checkbox screen does not have.
   *
   * It is a BUTTON rather than a checkbox on purpose. A checkbox would run inside `go()`,
   * after the confirm and the render, by which point the click's transient activation has
   * expired and `showSaveFilePicker` can no longer open — the save would silently fall
   * back to the Downloads folder and ignore Show Save As. Clicking here is a fresh
   * gesture, and `saveAs` opens the picker on it BEFORE fetching, so the prompt survives
   * the render. With Show Save As off it lands in Downloads exactly as V1 did.
   *
   * Not offered in `create` mode: the invoices do not exist yet. Once created, the page
   * moves to the invoice, where this dialog opens in `existing` mode and offers it.
   *
   * A CREDIT MEMO downloads the same way, and needs no `pdfName` from its host: the
   * options payload already carries the document number printed on the memo itself, so
   * the file is named after the document rather than after the record it was opened
   * from. It has no backup paperwork to merge (see `noDocuments`).
   */
  const canDownload = mode.kind !== 'create';
  const downloadNoun = mode.kind === 'creditmemo' ? 'Credit Memo'
    : mode.kind === 'estimate' ? 'Estimate' : 'Invoice';
  const download = async () => {
    if (saving || mode.kind === 'create') return;
    setSaving(true);
    setError('');
    try {
      const base = pdfName
        || (mode.kind === 'creditmemo'
            ? `credit-memo-${memo?.documentNumber || mode.recno}`
            : isEstimate ? 'invoice-estimate'
            : `invoice-${(mode as { invoiceHistoryRecno: number }).invoiceHistoryRecno}`);
      // The invoice alone and the invoice with its paperwork behind it are two different
      // documents. Saving both under one name overwrites one with the other, and the
      // clerk cannot tell afterwards which survived. The server names the same pair the
      // same way (invoice_creation._bundle_name) — this is the one place the FE has to
      // know the rule, because the picker opens BEFORE the fetch that would tell it.
      const suffix = mode.kind === 'existing' && picked.size > 0 ? '-paperwork' : '';
      await saveAs(`${(base + suffix).replace(/[^\w.-]+/g, '-')}.pdf`, () => (
        mode.kind === 'creditmemo' ? renderCreditMemo(mode.pronumber, mode.recno)
        : mode.kind === 'estimate' ? renderEstimate(mode.invoiceRecnos)
        // The ticked backup paperwork is merged into the saved file too, the same way it
        // is merged into what View shows and what the e-mail attaches.
        : printInvoices([(mode as { invoiceHistoryRecno: number }).invoiceHistoryRecno], [...picked])));
    } catch (e) {
      setError(message(e, mode.kind === 'creditmemo'
        ? 'Could not download the credit memo PDF.'
        : 'Could not download the invoice PDF.'));
    } finally {
      setSaving(false);
    }
  };

  const finish = (notes: string[], created: boolean, recnos: number[] = []) => {
    setBusy(false);
    running.current = false;
    const note = notes.join(' ');
    if (onDone) {
      onDone({ note, created, recnos });
      return;
    }
    setDone(note || 'Done.');
  };

  const go = async () => {
    if (!canGo || running.current) return;
    running.current = true;
    setBusy(true);
    setError('');
    // OPEN THE TAB NOW, BEFORE ANY await. A browser only honours window.open while the
    // click that caused it is still "transient user activation" — a few seconds, spent
    // by the first popup. Creating an invoice is a preview call, a create call and a PDF
    // fetch, which is well past that, so opening afterwards is blocked no matter what
    // the user has allow-listed. Open a blank tab on the gesture and navigate it when
    // the bytes arrive.
    // A print routed to a CUPS printer needs no tab at all: the bytes go to the server.
    const browserPrint = print && !sendToPrinter;
    const pdfTab = view || browserPrint ? window.open('', '_blank') : null;
    try {
      const fileRecnos = noDocuments ? [] : [...picked];
      const wantsEmail = toSelf || chosenEmails.length > 0 || (toOther && !!otherEmail.trim());
      const notes: string[] = [];
      // V1's `lpr -P $printer` for this document. The server resolves, spools and audits;
      // the note carries the CUPS job id so "it never came out" has something to chase.
      const spool = async (endpoint: string, extraBody: Record<string, unknown> = {}) => {
        const sent = await printingService.printTo(endpoint, { printer, copies: 1 }, extraBody);
        lastUsedPrinter.set('LASER', sent.printer || printer);
        notes.push(`Sent to ${sent.display_name} (job ${sent.job_id}).`);
      };
      // Navigate the already-open tab to a URL that carries its own name. Separate from
      // showPdf only in where the bytes come from; the pop-up handling is the same.
      const showPdfUrl = (url: string) => {
        if (!pdfTab || pdfTab.closed) {
          notes.push('The PDF was blocked by the pop-up blocker.');
          return;
        }
        pdfTab.location.href = url;
      };
      const showPdf = (blob: Blob) => {
        if (!pdfTab || pdfTab.closed) {
          notes.push('The PDF was blocked by the pop-up blocker.');
          return;
        }
        const url = URL.createObjectURL(blob);
        pdfTab.location.href = url;
        if (browserPrint) {
          // Print once the PDF has actually rendered; printing an empty tab prints nothing.
          pdfTab.addEventListener('load', () => pdfTab.print(), { once: true });
        }
        // Revoking immediately would race the new tab's own fetch of the blob.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      };
      const note = (sent: { redirected: boolean; recipients: string[]; intendedRecipients: string[] }) =>
        notes.push(sent.redirected
          ? `Test environment — sent to you (${sent.recipients.join(', ')}) instead of ${sent.intendedRecipients.join(', ')}.`
          : `Sent to ${sent.recipients.join(', ')}.`);

      // A credit memo creates nothing either, and its PDF is rendered from the record
      // each time — V1 drops the file in a temp directory and never records it.
      if (mode.kind === 'creditmemo') {
        const { pronumber, recno } = mode;
        if (view || browserPrint) showPdf(await renderCreditMemo(pronumber, recno));
        if (sendToPrinter) await spool(`/orders/${pronumber}/rebill-credit/${recno}/credit-memo/print`);
        if (wantsEmail) {
          note(await emailCreditMemo(pronumber, recno, {
            toSelf, toBillto: chosenEmails, toOther: toOther ? otherEmail.trim() : '',
          }));
        }
        finish(notes, false);
        return;
      }

      // An estimate creates nothing, so there is no confirm step and no invoice history
      // to act on afterwards — the PDF is rendered from the selection each time.
      if (isEstimate) {
        const recnos = (mode as { invoiceRecnos: number[] }).invoiceRecnos;
        if (view || print) showPdf(await renderEstimate(recnos));
        if (wantsEmail) {
          note(await emailEstimate({
            invoiceRecnos: recnos, toSelf, toBillto: chosenEmails,
            toOther: toOther ? otherEmail.trim() : '',
          }));
        }
        finish(notes, false);
        return;
      }

      const created = await onConfirm({
        view, print, toSelf, toBillto: chosenEmails, toOther: toOther ? otherEmail.trim() : '', fileRecnos,
        emailInvoices: canEmailEach && emailEach,
      });
      const targets = created.length
        ? created
        : mode.kind === 'existing' ? [mode.invoiceHistoryRecno] : [];
      // The ticked paperwork is merged into what is shown and printed, not only into an
      // e-mail — V1 has one code path for all three (invoicepdf.pl:1736).
      if (targets.length && (view || browserPrint)) {
        // VIEW and PRINT take different routes to the same PDF, on purpose.
        //
        // View navigates the tab to a named, time-limited URL, because a tab showing a
        // `URL.createObjectURL` blob has no name: the browser's PDF viewer offers the
        // blob URL's own UUID as the Save As filename, which is what clerks were saving.
        //
        // Print keeps the blob. The named URL is on the storage account's origin, and
        // `pdfTab.print()` on a cross-origin window is blocked — so routing Print through
        // it would trade a filename nobody reads for a print that never fires.
        if (browserPrint) showPdf(await printInvoices(targets, fileRecnos));
        else showPdfUrl((await invoiceViewUrl(targets, fileRecnos)).url);
      }
      // The same merged PDF Print-in-browser shows, spooled server-side instead. One
      // job for the whole selection: the server merges exactly as /invoice-list/print.
      if (targets.length && sendToPrinter) {
        await spool('/invoice-list/print-to-printer', { invoiceHistoryRecnos: targets, fileRecnos });
      }
      // After the invoices exist, and after View/Print, so a slow save does not sit in
      // front of the tab the user is waiting on. Named by the SERVER: the invoice number
      // does not exist until the invoices do, so there is nothing to name it from here.
      if (targets.length && saveAfterCreate) {
        const { blob, filename } = await printInvoicesNamed(targets, fileRecnos);
        await saveAs(filename, async () => blob);
        notes.push(`Saved ${filename}.`);
      }
      if (targets.length && wantsEmail) {
        for (const recno of targets) {
          note(await emailInvoice({
            invoiceHistoryRecno: recno, toSelf, toBillto: chosenEmails,
            toOther: toOther ? otherEmail.trim() : '', fileRecnos,
          }));
        }
      }
      // Only CREATE mode raises invoices. `existing` mode's onConfirm hands back the
      // invoice history recno it was already given, so a non-empty list is not evidence
      // that anything was created.
      finish(notes, mode.kind === 'create' && created.length > 0, created);
    } catch (e) {
      // Never leave an orphan blank tab: the work that was going to fill it failed.
      if (pdfTab && !pdfTab.closed && pdfTab.location.href === 'about:blank') pdfTab.close();
      setError(message(e, 'Could not complete the invoice options.'));
      setBusy(false);
      running.current = false;
    }
  };

  // Alt+S is Go — in every mode, so it creates, views/prints/e-mails an existing invoice,
  // renders an estimate or a credit memo, whichever this dialog was opened for — and
  // Alt+C is Cancel/Close: the app-wide pair. Each is bound only while its button is
  // clickable. The document preview sits on top without a hotkey layer of its own, so
  // both go quiet while it's open: Alt+S must not run Go behind it. The layer keeps the
  // chords off whatever opened this (the Pro save bar, the Rebill/Credit modal). Go opens
  // its PDF tab before any await; a keydown is a user gesture, so that holds from here.
  useAltHotkeys(
    {
      S: !previewDoc && !done && !loading && !busy && canGo ? () => void go() : null,
      C: previewDoc ? null : onClose,
    },
    undefined,
    { layer: true },
  );

  return (
    <div className={`fixed inset-0 ${layerClassName} flex items-center justify-center bg-scrim/50 p-4`} role="dialog" aria-modal="true" aria-label={title || 'Invoice options'}>
      {previewDoc && (
        <DocPreviewLightbox
          pronumber={previewDoc.pronumber}
          mapRecno={previewDoc.mapRecno}
          source="cms"
          filename={previewDoc.filename}
          filetype={previewDoc.filetype}
          onClose={() => setPreviewDoc(null)}
        />
      )}
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-rule bg-panel shadow-xl">
        <div className="border-b border-hairline px-4 py-3">
          <h2 className="text-base font-bold text-heading">{title}</h2>
        </div>

        {/* OUTSIDE the scrolling body. The unreadable-document refusal can name thirty
            files, and at the bottom of a scrolled list it was invisible — the user saw
            nothing happen. Here it is the first thing under the title, capped so a long
            list scrolls within itself instead of pushing the options off the screen.
            whitespace-pre-line: one file per line. */}
        {error && (
          <div className="max-h-48 shrink-0 overflow-y-auto border-b border-danger-fg/30 bg-danger px-4 py-3">
            <p role="alert" className="whitespace-pre-line text-sm text-danger-fg">{error}</p>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {loading && <p className="text-sm text-muted">Loading options…</p>}

          {!loading && memo && (
            <div className="rounded border border-rule bg-sunken px-3 py-2 text-sm text-body">
              <p>
                <span className="font-semibold">{memo.documentNumber}</span>
                {memo.billtoName && <> · {memo.billtoName}</>}
                {' · '}
                <span className="font-semibold">-${memo.total}</span>
              </p>
              {/* A credit memo number can be shared by more than one rebill/credit
                  record, and V1 renders ONE document covering all of them. Say so, so
                  nobody sends a two-record memo believing it is just the record they
                  opened. */}
              {memo.recordCount > 1 && (
                <p className="mt-1 text-warn-fg">
                  This credit memo number covers {memo.recordCount} rebill/credit records
                  {memo.pronumbers.length > 0
                    && <> on PRO {memo.pronumbers.join(', ')}</>}. All of them appear on
                  the document and are included in the total.
                </p>
              )}
            </div>
          )}

          {!loading && splits && (
            <div className="rounded border-l-4 border-warn-fg bg-warn px-3 py-2 text-sm text-body">
              {/* The outcome, not the policy. "This Bill To is invoiced one charge at a
                  time" described the configuration and left the user to work out what
                  pressing the button would do; the number is the thing they need.

                  "SET OF CHARGES", NOT "CHARGE". The unit is a `cms.invoice` record, and
                  one of those carries several `invoice_lineitem` rows — container dray,
                  fuel surcharge and chassis management each with their own rate, quantity
                  and total. Calling it a charge reads as a single line item, which is the
                  same mis-naming that made `invoiceperpro` wrong. */}
              <p className="font-semibold">
                {willCreate === undefined
                  ? 'Each set of charges on this Bill To is invoiced separately.'
                  : `This will create ${willCreate} ${willCreate === 1 ? 'invoice' : 'invoices'} — one for each set of charges.`}
              </p>
              <p className="mt-1">Each gets its own invoice number and PDF.</p>
              {!!willHold && (
                <p className="mt-1">
                  {willHold} {willHold === 1 ? 'set of charges is' : 'sets of charges are'} on
                  hold and will not be invoiced. {willHold === 1 ? 'It stays' : 'They stay'} on
                  this screen to fix and run again.
                </p>
              )}
              {!canEmailEach && <p className="mt-1">These will not print or e-mail.</p>}
            </div>
          )}

          {/* The count and the recipients, spelled out. This is the last point at which a
              mistake is cheap: past the button it is one message per invoice, already sent,
              and cancelling is one invoice at a time by design. */}
          {!loading && canEmailEach && (
            <fieldset className="space-y-2">
              <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">Email</legend>
              <Check checked={emailEach} onChange={setEmailEach}>
                Email each invoice to the Bill To
                {emails.length > 0 && <span className="text-muted"> [{emails.join(', ')}]</span>}
              </Check>
              {emailEach && (
                <p className="text-xs text-muted">
                  One message per invoice{willCreate === undefined ? '' : ` — ${willCreate} in all`},
                  each carrying its own PDF. Sending continues in the background after this
                  closes, so you do not have to wait on it.
                </p>
              )}
            </fieldset>
          )}

          {!loading && !splits && (
            <>
              <fieldset className="space-y-2">
                <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">Actions</legend>
                <Check checked={view} onChange={setView}>View Invoice</Check>
                <Check checked={print} onChange={setPrint}>Print Invoice</Check>
                {print && canSendToPrinter && (
                  <div className="ml-6 space-y-2" data-testid="print-target">
                    <div className="flex flex-wrap gap-4 text-sm text-body">
                      <label className="flex items-center gap-1.5">
                        <input type="radio" name="print-target" value="browser"
                          checked={printTarget === 'browser'} onChange={() => setPrintTarget('browser')} />
                        Browser print dialog
                      </label>
                      <label className="flex items-center gap-1.5">
                        <input type="radio" name="print-target" value="printer"
                          checked={printTarget === 'printer'} onChange={() => setPrintTarget('printer')} />
                        Send to printer
                      </label>
                    </div>
                    {printTarget === 'printer' && (
                      <PrinterSelect type="LASER" value={printer} onChange={setPrinter} size="sm" disabled={busy} />
                    )}
                  </div>
                )}
                {/* Only in create mode. Every other mode has the Download PDF button,
                    which can open the save picker on its own click; here the invoices do
                    not exist until Go runs, so this is the only place it can be offered. */}
                {mode.kind === 'create' && (
                  <Check checked={saveAfterCreate} onChange={setSaveAfterCreate}>
                    Download Invoice
                  </Check>
                )}
                <Check checked={toSelf} onChange={setToSelf}>
                  Email Myself {data?.userEmail && <span className="text-muted">[{data.userEmail}]</span>}
                </Check>
                <Check checked={toBillto} onChange={setToBillto} disabled={!emails.length}>
                  Email to Bill To {!emails.length && <span className="text-muted">[n/a]</span>}
                </Check>
                {toBillto && emails.length > 0 && (
                  <div className="ml-6 space-y-1">
                    {emails.length > 1 && (
                      <Check
                        checked={billtoPicked.length === emails.length}
                        onChange={(on) => setBilltoPicked(on ? [...emails] : [])}
                      >
                        All
                      </Check>
                    )}
                    {emails.map((address) => (
                      <Check
                        key={address}
                        checked={billtoPicked.includes(address)}
                        onChange={(on) => setBilltoPicked((c) => (on ? [...c, address] : c.filter((a) => a !== address)))}
                      >
                        {address}
                      </Check>
                    ))}
                  </div>
                )}
                <Check checked={toOther} onChange={setToOther}>Email to Other</Check>
                {toOther && (
                  <input
                    type="text"
                    value={otherEmail}
                    onChange={(e) => setOtherEmail(e.target.value)}
                    placeholder="name@example.com, second@example.com"
                    aria-label="Other email addresses"
                    className="ml-6 w-[calc(100%-1.5rem)] rounded border border-rule bg-input px-2 py-1 text-sm text-body"
                  />
                )}
              </fieldset>

              {!noDocuments && allDocs.length > 0 && (
                <fieldset className="space-y-2">
                  <legend className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
                    Documents ({allDocs.length})
                  </legend>
                  <Check
                    checked={picked.size === allDocs.length && allDocs.length > 0}
                    onChange={(on) => toggleDocs(allDocs.map((d) => d.fileRecno), on)}
                  >
                    <span className="font-semibold">ALL DOCUMENTS</span>
                  </Check>
                  {(data?.documentTypes ?? []).map((type) => {
                    const ofType = allDocs.filter((d) => d.documenttype === type);
                    return (
                      <Check
                        key={type}
                        checked={ofType.every((d) => picked.has(d.fileRecno))}
                        onChange={(on) => toggleDocs(ofType.map((d) => d.fileRecno), on)}
                      >
                        ALL {type}S
                      </Check>
                    );
                  })}
                  <div className="space-y-3 pt-1">
                    {(data?.documents ?? []).map((pro) => (
                      <div key={pro.pronumber}>
                        <div className="mb-1 rounded bg-sunken px-2 py-1 text-xs font-bold text-heading">
                          Pro {pro.pronumber}
                        </div>
                        {pro.documents.length === 0 && (
                          <p className="px-2 text-xs text-muted">No backup paperwork.</p>
                        )}
                        {pro.documents.map((doc) => (
                          <div key={doc.fileRecno} className="flex items-center gap-1 px-2">
                            {/* The Docs tab's own preview control, not a second one that
                                looks like it: same icon, same lightbox, same route. A
                                biller ticks paperwork onto an invoice without being able
                                to see what the document IS, and a wrong one goes to the
                                customer behind the invoice. */}
                            <DocFileIconButton
                              filetype={doc.filetype}
                              onPreview={() => setPreviewDoc(doc)}
                              testId={`invoice-options-preview-${doc.fileRecno}`}
                            />
                            <Check
                              checked={picked.has(doc.fileRecno)}
                              onChange={(on) => toggleDocs([doc.fileRecno], on)}
                            >
                              <span className="font-medium">{doc.documenttype}</span>
                              <span className="text-muted"> · {doc.filetype} · {doc.uploaded.slice(0, 10)}</span>
                              {doc.description && <span className="text-muted"> · {doc.description}</span>}
                            </Check>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </fieldset>
              )}
            </>
          )}

          {done && <p role="status" className="text-sm text-ok-fg">{done}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-hairline bg-sunken px-4 py-3">
          {canDownload && (
            <button
              type="button"
              onClick={() => void download()}
              disabled={loading || busy || saving}
              // Left of the dismiss/confirm pair: it neither closes the dialog nor is the
              // thing the Go button does, and it stays available on its own.
              className="mr-auto rounded-md border border-rule px-3 py-2 text-sm font-medium text-label hover:bg-panel disabled:cursor-not-allowed disabled:opacity-50"
            >
              {/* Says the same thing as create mode's "Download Invoice" tick-box, so the
                  two read as one idea rather than two features. They stay separate
                  CONTROLS for the reason above: a dedicated click is what lets the save
                  picker open, and that is what a clerk filing into a per-customer folder
                  is relying on. */}
              {saving ? 'Saving…' : `Download ${downloadNoun}`}
            </button>
          )}
          <button type="button" onClick={onClose} title={`${done ? 'Close' : 'Cancel'} (Alt+C)`}
            aria-keyshortcuts="Alt+C" className="px-3 py-2 text-sm text-label underline">
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done && (
            <button
              type="button"
              onClick={go}
              disabled={loading || busy || !canGo}
              title={`${splits ? 'Create Invoices' : 'Go'} (Alt+S)`}
              aria-keyshortcuts="Alt+S"
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Working…' : splits ? 'Create Invoices' : 'Go'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceOptionsDialog;
